import { useState, useEffect } from 'react';
import {
  Outlet,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { SpaceProvider } from './contexts/space';

// layouts

import MainLayout from './layouts/Main';
import ThemeLayout from './layouts/Theme';

// modals

import EditAvatarModal from './modals/EditAvatar';
import EditEmailModal from './modals/EditEmail';
import EditNameModal from './modals/EditName';
import EditSpaceBannerModal from './modals/EditSpaceBanner';
import EditSpaceIconModal from './modals/EditSpaceIcon';
import EditSpaceNameModal from './modals/EditSpaceName';
import EditSpaceSlugModal from './modals/EditSpaceSlug';
import DeleteAccountModal from './modals/DeleteAccount';
import DeleteSpaceModal from './modals/DeleteSpace';
import InviteLinkModal from './modals/InviteLink';
import JoinSpaceModal from './modals/JoinSpace';
import KeyboardShortcutsModal from './modals/KeyboardShortcuts';
import LoginModal from './modals/Login';
import LogoutModal from './modals/Logout';
import NewSpaceModal from './modals/NewSpace';
import NewTagModal from './modals/NewTag';
import NewTopicModal from './modals/NewTopic';
import SearchModal from './modals/Search';
import UpgradeModal from './modals/Upgrade';

// pages

import NotFoundPage from './pages/NotFound';
import SettingsPage from './pages/Settings';
import SettingsAppearancePage from './pages/SettingsAppearance';
import SettingsDataPage from './pages/SettingsData';
import SettingsMainPage from './pages/SettingsMain';
import SettingsSessionsPage from './pages/SettingsSessions';
import SettingsSpacesPage from './pages/SettingsSpaces';
import SpacePage from './pages/Space';
import SpaceSettingsPage from './pages/SpaceSettings';
import SpaceSettingsArchivesPage from './pages/SpaceSettingsArchives';
import SpaceSettingsDataPage from './pages/SpaceSettingsData';
import SpaceSettingsMainPage from './pages/SpaceSettingsMain';
import SpaceSettingsMembersPage from './pages/SpaceSettingsMembers';
import SpaceSettingsPlanPage from './pages/SpaceSettingsPlan';
import SpaceSettingsPreferencesPage from './pages/SpaceSettingsPreferences';
import SpaceSettingsTagsPage from './pages/SpaceSettingsTags';
import TopicPage from './pages/Topic';

const SpaceProviderComponent = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // 16 = bottom padding
  const paginationLimit = Math.ceil(
    (document.documentElement.clientHeight - 16) / 40
  );

  const vars = {
    authorId:
      searchParams.get('author') || location.state?.filters?.author || null,
    state: searchParams.get('state') || location.state?.filters?.state || null,
    assigneeId:
      searchParams.get('assignee') || location.state?.filters?.assignee || null,
    tagId: searchParams.get('tag') || location.state?.filters?.tag || null,
    limit: paginationLimit,
  };

  const [spaceVars, setSpaceVars] = useState({
    ...vars,
  });

  useEffect(() => {
    setSpaceVars({
      ...vars,
    });
  }, [
    document.documentElement.clientHeight,
    location.state?.filters,
    searchParams,
  ]);

  return (
    <SpaceProvider
      value={{
        spaceVars,
        setSpaceVars,
      }}
    >
      <Outlet />
    </SpaceProvider>
  );
};

const Path = () => {
  const location = useLocation();

  return (
    <>
      <Routes location={location.state?.backgroundLocation || location}>
        <Route path="/">
          {import.meta.env.MODE === 'development' && (
            <Route index element={<Navigate to="/login" />} />
          )}
          <Route element={<ThemeLayout />}>
            <Route element={<SpaceProviderComponent />}>
              <Route element={<MainLayout />}>
                <Route path="s">
                  <Route index element={<NotFoundPage />} />
                  <Route path=":s">
                    <Route index element={<SpacePage />} />
                    <Route path="t">
                      <Route index element={<NotFoundPage />} />
                      <Route path=":t" element={<TopicPage />} />
                      <Route path="new" element={<NewTopicModal />} />
                    </Route>
                    <Route path="search" element={<SearchModal />} />
                    <Route path="settings" element={<SpaceSettingsPage />}>
                      <Route path="main" element={<SpaceSettingsMainPage />}>
                        <Route
                          path="edit-space-banner"
                          element={<EditSpaceBannerModal />}
                        />
                        <Route
                          path="edit-space-icon"
                          element={<EditSpaceIconModal />}
                        />
                        <Route
                          path="edit-space-name"
                          element={<EditSpaceNameModal />}
                        />
                        <Route
                          path="edit-space-slug"
                          element={<EditSpaceSlugModal />}
                        />
                      </Route>
                      <Route path="plan" element={<SpaceSettingsPlanPage />}>
                        <Route path="upgrade" element={<UpgradeModal />} />
                      </Route>
                      <Route path="tags" element={<SpaceSettingsTagsPage />}>
                        <Route path="new" element={<NewTagModal />} />
                      </Route>
                      <Route path="data" element={<SpaceSettingsDataPage />}>
                        <Route
                          path="delete-space"
                          element={<DeleteSpaceModal />}
                        />
                      </Route>
                      <Route
                        path="members"
                        element={<SpaceSettingsMembersPage />}
                      >
                        <Route
                          path="invite-link"
                          element={<InviteLinkModal />}
                        />
                      </Route>
                      <Route
                        path="archives"
                        element={<SpaceSettingsArchivesPage />}
                      />
                      <Route
                        path="preferences"
                        element={<SpaceSettingsPreferencesPage />}
                      />
                    </Route>
                  </Route>
                  <Route path="new" element={<NewSpaceModal />} />
                </Route>
                <Route path="settings" element={<SettingsPage />}>
                  <Route path="main" element={<SettingsMainPage />}>
                    <Route path="edit-name" element={<EditNameModal />} />
                    <Route path="edit-email" element={<EditEmailModal />} />
                    <Route path="edit-avatar" element={<EditAvatarModal />} />
                  </Route>
                  <Route path="data" element={<SettingsDataPage />}>
                    <Route
                      path="delete-account"
                      element={<DeleteAccountModal />}
                    />
                  </Route>
                  <Route path="sessions" element={<SettingsSessionsPage />} />
                  <Route path="spaces" element={<SettingsSpacesPage />} />
                  <Route
                    path="appearance"
                    element={<SettingsAppearancePage />}
                  />
                </Route>
                <Route
                  path="keyboard-shortcuts"
                  element={<KeyboardShortcutsModal />}
                />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="join">
          <Route element={<MainLayout />}>
            <Route index element={<NotFoundPage />} />
          </Route>
          <Route path=":code" element={<JoinSpaceModal />} />
        </Route>
        <Route path="login" element={<LoginModal />} />
        <Route path="logout" element={<LogoutModal />} />
      </Routes>
      {location.state?.backgroundLocation && (
        <Routes>
          {localStorage.getItem('nightMode') === 'true' ? (
            <Route element={<ThemeLayout />}>
              <Route path="/s/:s/search" element={<SearchModal />} />
              <Route path="/s/new" element={<NewSpaceModal />} />
              <Route element={<SpaceProviderComponent />}>
                <Route path="/s/:s/t/new" element={<NewTopicModal />} />
              </Route>
              <Route path="/login" element={<LoginModal />} />
              <Route path="/logout" element={<LogoutModal />} />
              <Route
                path="/keyboard-shortcuts"
                element={<KeyboardShortcutsModal />}
              />
            </Route>
          ) : (
            <>
              <Route path="/s/:s/search" element={<SearchModal />} />
              <Route path="/s/new" element={<NewSpaceModal />} />
              <Route element={<SpaceProviderComponent />}>
                <Route path="/s/:s/t/new" element={<NewTopicModal />} />
              </Route>
              <Route path="/login" element={<LoginModal />} />
              <Route path="/logout" element={<LogoutModal />} />
              <Route
                path="/keyboard-shortcuts"
                element={<KeyboardShortcutsModal />}
              />
            </>
          )}
        </Routes>
      )}
    </>
  );
};

export default Path;
